import {
  MarkerLabelConfigTypeEnum,
  PointOfInterest,
  PointOfInterestTemplate,
} from '@/__generated__/graphql';
import {
  POI_MARKER_LABEL_BACKGROUND_COLOR,
  POI_MARKER_LABEL_BLACK_TEXT_COLOR,
  POI_MARKER_LABEL_WHITE_BACKGROUND_COLOR,
} from '@/theme/colors';
import { Box, Stack } from '@mui/material';
import { Arrow, Content, Detail, DetailLabel, DetailValue, Ellipse, Title } from './styles';
import { usePoiMarkerLabelData } from './usePoiMarkerLabelData';
import { NOT_APPLICABLE_LABEL } from '@/constants';

export const PointOfInterestMarkerLabel = ({
  pointOfInterest,
  height = 0,
  scale = 1,
  swivel = false,
  selectedSpherical = false,
  selected = false,
  customTemplate,
}: {
  pointOfInterest: PointOfInterest;
  height?: number;
  scale?: number;
  swivel?: boolean;
  selectedSpherical?: boolean;
  selected?: boolean;
  customTemplate?: Partial<PointOfInterestTemplate>;
}) => {
  const {
    name,
    background,
    barHeight,
    randomAngle,
    hideName,
    headerField,
    headerFieldValue,
    markerLabelFields,
    variant,
  } = usePoiMarkerLabelData({
    pointOfInterest,
    height,
    swivel,
    customTemplate,
  });

  return (
    <Stack
      data-testid="poi-marker-label-container"
      sx={{
        userSelect: 'none',
        width: 'fit-content',
        minWidth: 'fit-content',
        marginBottom: `${barHeight + (selectedSpherical ? 70 : 100) + (selected ? 20 : 0)}px`,
        transform: swivel ? `rotate(${randomAngle}deg)` : '',
        transformOrigin: 'bottom',
      }}
    >
      <Content
        background={background}
        data-testid="poi-marker-label-content"
        variant={variant}
        sx={{
          transform: `${swivel ? `rotate(${randomAngle * -1}deg)` : ''} scale(${scale})`,
          transformOrigin: 'bottom',
        }}
      >
        {(!hideName || headerField) && (
          <Stack direction="row" alignItems="center" gap="2.4rem" justifyContent="space-between">
            {!hideName && (
              <Title color={background} data-testid="poi-marker-label-title">
                {name}
              </Title>
            )}
            {headerField && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="0.4rem"
              >
                {headerFieldValue?.color && (
                  <Ellipse
                    data-testid="poi-marker-label-category-indicator"
                    sx={{
                      backgroundColor: headerFieldValue.color,
                    }}
                  />
                )}
                <DetailLabel color={background} data-testid="poi-marker-label-category-label">
                  {headerFieldValue?.value ?? NOT_APPLICABLE_LABEL}
                </DetailLabel>
              </Stack>
            )}
          </Stack>
        )}
        {variant === MarkerLabelConfigTypeEnum.Detailed && (
          <Stack marginTop={'0.8rem'} gap="0.4rem">
            {markerLabelFields.map((field) => (
              <Detail key={field.name}>
                <DetailLabel color={background} data-testid="poi-marker-label-detail-label">
                  {field.name}
                </DetailLabel>
                <DetailValue color={background} data-testid="poi-marker-label-detail-value">
                  {field.value ?? NOT_APPLICABLE_LABEL}
                </DetailValue>
              </Detail>
            ))}
          </Stack>
        )}
      </Content>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Arrow
          data-testid="poi-marker-label-arrow"
          background={background}
          sx={{
            transform: swivel ? `rotate(${randomAngle * -1}deg)` : '',
            transformOrigin: 'top',
          }}
        />
        <Box
          data-testid="poi-marker-label-bar"
          sx={{
            height: `${barHeight}px`,
            width: `calc(0.1rem * ${scale})`,
            backgroundColor:
              background === POI_MARKER_LABEL_BACKGROUND_COLOR
                ? POI_MARKER_LABEL_WHITE_BACKGROUND_COLOR
                : POI_MARKER_LABEL_BLACK_TEXT_COLOR,
          }}
        />
      </Box>
    </Stack>
  );
};
